import { BiCardQuery } from '@euler/model/bi/blend';
import {
  BiDashboardOp,
  BiDashboardOpKind,
  BiUpdateCardOp,
} from '@euler/model/bi/dashboard';
import { BiVizSpec } from '@euler/model/bi/viz';
import { useServiceFactory } from '@euler/services/factory';
import deepEqual from 'deep-equal';
import { useCallback } from 'react';
import { biErrorMessage } from '../../functions/biErrors';
import { BiEditorTarget } from '../editor/functions/biEditorState';
import { BiApplyOutcome } from './useBiDashboard';

const same = (a: unknown, b: unknown) => deepEqual(a, b, { strict: true });

/**
 * Saves the card editor: an edit is one `update_card` carrying only what
 * changed; a new card becomes a draft first, then `add_card` — the same
 * path a card added from a chat takes.
 */
export function useBiCardEditing(
  apply: (
    ops: BiDashboardOp[],
    text: string,
    baseRevision?: number,
  ) => Promise<BiApplyOutcome>,
) {
  const { biService } = useServiceFactory();
  return useCallback(
    async (
      target: BiEditorTarget,
      input: { spec: BiCardQuery; viz: BiVizSpec; title: string },
    ): Promise<BiApplyOutcome | { ok: true }> => {
      if (target.kind === 'edit') {
        const { card } = target;
        const op: BiUpdateCardOp = {
          op: BiDashboardOpKind.UpdateCard,
          cardId: card.id,
        };
        if (input.title !== card.title) op.title = input.title;
        if (!same(input.spec, card.spec)) op.querySpec = input.spec;
        if (!same(input.viz, card.viz)) op.vizSpec = input.viz;
        if (Object.keys(op).length === 2) return { ok: true };
        // Against the copy the form opened on: a card changed meanwhile is
        // refused as stale rather than overwritten.
        return await apply([op], '已更新图表', target.revision);
      }
      let draftId: string | undefined = undefined;
      try {
        draftId = (await biService.createDraft(input)).id;
      } catch (error) {
        console.error('BI draft for a new card failed', error);
        return {
          ok: false,
          message: biErrorMessage(error, '图表没有建成，请稍后重试。'),
        };
      }
      return await apply(
        [{ op: BiDashboardOpKind.AddCard, draftId }],
        '已添加图表',
      );
    },
    [apply, biService],
  );
}
